import React, { useEffect, useState } from 'react';
import { X, Sparkles, Bug, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { aiDebugService, AiDebugLog } from '@/services/ai-analytics.service';

interface Props {
  /** LeadEvent uid (matches the AI message id in the transcript). */
  leadEventUid: string | null;
  onClose: () => void;
}

/**
 * Slide-over panel that fetches the captured AI debug snapshot for one outbound
 * AI reply. Shows: provider/model, full system prompt, the transcript that
 * was sent, raw first-reply text, tool calls + execution results, final reply,
 * and token totals.
 *
 * Admin-only — gating happens at the caller site (it just renders an overlay
 * when `leadEventUid` is non-null).
 */
export const AiDebugPanel: React.FC<Props> = ({ leadEventUid, onClose }) => {
  const [data, setData] = useState<AiDebugLog | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Each major section is collapsible — system prompts can be huge.
  const [openSystem, setOpenSystem] = useState(true);
  const [openTranscript, setOpenTranscript] = useState(false);
  const [openTools, setOpenTools] = useState(true);
  const [openOutput, setOpenOutput] = useState(true);

  useEffect(() => {
    if (!leadEventUid) return;
    let alive = true;
    setLoading(true);
    setError(null);
    setData(null);
    aiDebugService
      .getByLeadEvent(leadEventUid)
      .then((r) => { if (alive) setData(r); })
      .catch((e: unknown) => {
        if (alive) {
          const status = (e as { response?: { status?: number } })?.response?.status;
          if (status === 404) setError('No debug snapshot was captured for this message.');
          else setError(e instanceof Error ? e.message : 'Failed to load debug log');
        }
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [leadEventUid]);

  if (!leadEventUid) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex h-full w-full max-w-xl flex-col bg-surface border-l border-border shadow-elevated animate-slide-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-3.5">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-500/10 text-violet-500">
              <Bug size={15} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-text-primary">AI debug snapshot</h3>
              <p className="text-2xs text-text-muted">
                What context the model saw, what it returned, what tools fired
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary"
          >
            <X size={15} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {loading && (
            <div className="py-12 text-center text-sm text-text-muted">Loading…</div>
          )}

          {error && !loading && (
            <div className="flex items-start gap-2.5 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2.5 text-xs">
              <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warning" />
              <div>
                <div className="font-semibold text-text-primary">Couldn&apos;t load debug snapshot</div>
                <p className="mt-0.5 text-text-muted">{error}</p>
                <p className="mt-1.5 text-2xs text-text-muted">
                  Snapshots are written from this point on — older AI replies won&apos;t have one.
                </p>
              </div>
            </div>
          )}

          {data && !loading && !error && (
            <>
              {/* Meta row */}
              <div className="grid grid-cols-2 gap-3 text-xs">
                <Tile label="Provider" value={data.provider ?? '—'} />
                <Tile label="Model" value={data.model ?? '—'} />
                <Tile label="Tokens in" value={data.tokensIn.toLocaleString()} />
                <Tile label="Tokens out" value={data.tokensOut.toLocaleString()} />
              </div>

              {/* System prompt */}
              <Section
                title="System prompt"
                count={data.systemPrompt.length}
                countLabel="chars"
                open={openSystem}
                onToggle={() => setOpenSystem((v) => !v)}
              >
                <pre className="whitespace-pre-wrap break-words text-2xs leading-snug text-text-secondary font-mono">
                  {data.systemPrompt || '(empty)'}
                </pre>
              </Section>

              {/* Transcript */}
              <Section
                title="Transcript passed to LLM"
                count={data.transcript.length}
                countLabel="messages"
                open={openTranscript}
                onToggle={() => setOpenTranscript((v) => !v)}
              >
                {data.transcript.length === 0 ? (
                  <p className="text-2xs text-text-muted italic">(no prior turns — first contact)</p>
                ) : (
                  <div className="space-y-2">
                    {data.transcript.map((m, i) => (
                      <div key={i} className="rounded-md border border-border bg-surface-alt/40 p-2">
                        <div className="mb-1 flex items-center gap-2 text-2xs">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 font-semibold capitalize ${
                              m.role === 'user'
                                ? 'bg-info-soft text-info'
                                : m.role === 'assistant'
                                ? 'bg-violet-500/10 text-violet-600'
                                : m.role === 'tool'
                                ? 'bg-warning-soft text-warning'
                                : 'bg-surface-alt text-text-muted'
                            }`}
                          >
                            {m.role}
                          </span>
                          {m.toolCallId && (
                            <span className="font-mono text-[10px] text-text-muted">
                              {m.toolCallId.slice(0, 12)}…
                            </span>
                          )}
                        </div>
                        <pre className="whitespace-pre-wrap break-words text-2xs text-text-primary">
                          {m.content ?? '(no content)'}
                        </pre>
                      </div>
                    ))}
                  </div>
                )}
              </Section>

              {/* Tools */}
              <Section
                title="Tools"
                count={data.firstReplyToolCalls.length}
                countLabel="calls"
                open={openTools}
                onToggle={() => setOpenTools((v) => !v)}
              >
                {data.toolsOffered.length > 0 && (
                  <div className="mb-3">
                    <div className="mb-1 text-2xs font-medium uppercase tracking-wider text-text-muted">
                      Offered ({data.toolsOffered.length})
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {data.toolsOffered.map((t) => (
                        <span
                          key={t.name}
                          className="rounded-full border border-border bg-surface-alt px-2 py-0.5 text-2xs text-text-secondary"
                          title={t.description ?? ''}
                        >
                          {t.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {data.firstReplyToolCalls.length === 0 ? (
                  <p className="text-2xs text-text-muted italic">No tool calls — model replied directly.</p>
                ) : (
                  <div className="space-y-2">
                    {data.firstReplyToolCalls.map((c, i) => {
                      const result = data.toolResults[i]?.result;
                      const ok = result?.ok;
                      return (
                        <div key={i} className="rounded-md border border-border bg-surface-alt/40 p-2.5">
                          <div className="mb-1.5 flex items-center justify-between text-xs">
                            <span className="font-mono font-semibold text-text-primary">{c.name}</span>
                            {result && (
                              <span
                                className={`inline-flex rounded-full px-2 py-0.5 text-2xs font-semibold ${
                                  ok ? 'bg-success-soft text-success' : 'bg-danger-soft text-danger'
                                }`}
                              >
                                {ok ? 'ok' : 'failed'}
                              </span>
                            )}
                          </div>
                          <div className="mb-1 text-2xs font-medium uppercase tracking-wider text-text-muted">Args</div>
                          <pre className="whitespace-pre-wrap break-words text-2xs text-text-primary">
                            {JSON.stringify(c.arguments, null, 2)}
                          </pre>
                          {result && (
                            <>
                              <div className="mt-2 mb-1 text-2xs font-medium uppercase tracking-wider text-text-muted">Result</div>
                              <p className="text-2xs text-text-secondary">{result.message}</p>
                              {result.recordUid && (
                                <p className="mt-0.5 text-2xs text-text-muted font-mono">
                                  recordUid: {result.recordUid}
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Section>

              {/* Output */}
              <Section
                title="Model output"
                open={openOutput}
                onToggle={() => setOpenOutput((v) => !v)}
              >
                {data.firstReplyText && (
                  <div className="mb-3">
                    <div className="mb-1 text-2xs font-medium uppercase tracking-wider text-text-muted">
                      First reply (raw)
                    </div>
                    <pre className="whitespace-pre-wrap break-words rounded-md bg-surface-alt/40 p-2 text-2xs text-text-secondary">
                      {data.firstReplyText}
                    </pre>
                  </div>
                )}
                {data.finalReplyText && data.finalReplyText !== data.firstReplyText && (
                  <div>
                    <div className="mb-1 flex items-center gap-1.5 text-2xs font-medium uppercase tracking-wider text-violet-500">
                      <Sparkles size={11} />
                      Final reply sent to customer
                    </div>
                    <pre className="whitespace-pre-wrap break-words rounded-md border border-violet-500/30 bg-violet-500/5 p-2 text-2xs text-text-primary">
                      {data.finalReplyText}
                    </pre>
                  </div>
                )}
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-surface-alt/40 px-3 py-2">
      <div className="text-2xs font-medium uppercase tracking-wider text-text-muted">{label}</div>
      <div className="mt-0.5 text-sm font-semibold text-text-primary tabular-nums">{value}</div>
    </div>
  );
}

interface SectionProps {
  title: string;
  count?: number;
  countLabel?: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}
function Section({ title, count, countLabel, open, onToggle, children }: SectionProps) {
  return (
    <div className="rounded-lg border border-border">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-3 py-2 text-left transition-colors hover:bg-surface-alt/40"
      >
        <span className="flex items-center gap-1.5 text-xs font-semibold text-text-primary">
          {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          {title}
        </span>
        {typeof count === 'number' && (
          <span className="text-2xs text-text-muted tabular-nums">
            {count.toLocaleString()} {countLabel}
          </span>
        )}
      </button>
      {open && <div className="border-t border-border px-3 py-2.5">{children}</div>}
    </div>
  );
}
